import { Button, Card, Checkbox, Form, Input, InputNumber, Space } from "antd";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    Button as ZhButton,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    ZhMap,
    type ZhMapRef
} from "zhihao-ui-react";

import request from "@/api";
import type { ConsignmentFormValues, ConsignmentRow } from "./types";

export interface ConsignmentFormPageProps {
    loadConsignment?: (id: number | string) => Promise<ConsignmentRow>;
    saveConsignment?: (id: number | string | undefined, values: ConsignmentFormValues) => Promise<void>;
    submitConsignment?: (id: number | string | undefined, values: ConsignmentFormValues) => Promise<void>;
}

interface ConsignmentUploadedFile {
    id?: number;
    path?: string;
}

const CONSIGNMENT_IMAGE_TYPES = ["image/jpeg", "image/png"];
const CONSIGNMENT_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-matroska"];
const CONSIGNMENT_IMAGE_MAX_SIZE = 2 * 1024 * 1024;
const CONSIGNMENT_VIDEO_MAX_SIZE = 200 * 1024 * 1024;

const DEFAULT_MAP_CENTER: [number, number] = [114.84, 30.52];

/** 解析 Vue geo 对象使用的经纬度文本。 */
function parseGeoPoint(value?: string): [number, number] | undefined {
    const point = value?.split(",").map(item => Number(item.trim()));
    if (!point || point.length < 2 || point.some(item => !Number.isFinite(item))) return undefined;
    return [point[0], point[1]];
}

/** 保留旧地图选择器展示的六位小数格式。 */
function formatGeoPoint(point?: readonly number[]): string {
    if (!point || point.length < 2) return "";
    return `${point[0].toFixed(6)}, ${point[1].toFixed(6)}`;
}

/** 复用 Vue 寄售商品详情接口，为编辑表单回填数据。 */
export async function loadConsignment(id: number | string): Promise<ConsignmentRow> {
    const response = await request.service.get<ConsignmentRow>(`/admin/product-consignment/vo/${id}`);
    return response.data;
}

/** 复用 Vue 寄售商品新增草稿和更新草稿接口。 */
export async function saveConsignment(id: number | string | undefined, values: ConsignmentFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/product-consignment/create-save", values);
    } else {
        await request.service.post(`/admin/product-consignment/update-save/${id}`, values);
    }
}

/** 复用 Vue 寄售商品正式提交接口，新增使用 POST，编辑使用 PUT。 */
export async function submitConsignment(id: number | string | undefined, values: ConsignmentFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/product-consignment", values);
    } else {
        await request.service.put(`/admin/product-consignment/${id}`, values);
    }
}

/** 寄售商品新增/编辑页，覆盖 Vue 表单的核心价格、库存、起订量和介绍字段。 */
export function ConsignmentFormPage({
    loadConsignment: load = loadConsignment,
    saveConsignment: save = saveConsignment,
    submitConsignment: submitForm = submitConsignment
}: ConsignmentFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const id = query.get("id") || undefined;
    const [form] = Form.useForm<ConsignmentFormValues>();
    const isIncludeTax = Form.useWatch("isIncludeTax", form);
    const settlementMethod = Form.useWatch("settlementMethod", form);
    const depositPaymentMethod = Form.useWatch("depositPaymentMethod", form);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [mainImagePath, setMainImagePath] = useState<string | null>(null);
    const [videoPath, setVideoPath] = useState<string | null>(null);
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [mapDraft, setMapDraft] = useState("");
    const mapRef = useRef<ZhMapRef>(null);

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({ title: "", weight: 0 });
            return;
        }

        void load(id)
            .then(result => {
                setErrorMessage(null);
                const values = { ...result.productConsignment } as ConsignmentFormValues;
                // Vue 接口只返回 payType，编辑时还原为表单使用的多选数组。
                const normalizedPayType = values.payType == null ? undefined : Number(values.payType);
                if (normalizedPayType != null) {
                    values.payType = normalizedPayType;
                    values.payTypes = normalizedPayType === 3 ? [1, 2] : [normalizedPayType];
                }
                // Vue 返回数组，快速开发表单使用文本控件回显，提交时再还原数组。
                if (Array.isArray(values.parameterInfo)) {
                    values.parameterInfoText = JSON.stringify(values.parameterInfo);
                }
                if (Array.isArray(values.productLabel)) {
                    values.productLabelText = values.productLabel.join(",");
                }
                if (Array.isArray(values.handlerMobile)) {
                    values.handlerMobileText = values.handlerMobile.join(",");
                }
                if (values.geo) {
                    values.geoLng = values.geo.lng == null ? "" : String(values.geo.lng);
                    values.geoLat = values.geo.lat == null ? "" : String(values.geo.lat);
                }
                // 编辑回显沿用 Vue 详情中的文件地址；新上传成功后会覆盖对应展示。
                setMainImagePath(result.mainFile?.path || null);
                setVideoPath(result.videoFile?.path || null);
                form.setFieldsValue(values);
            })
            .catch(() => setErrorMessage("寄售商品详情加载失败"));
    }, [form, id, load]);

    /** 按 Vue upload-list 契约上传媒体，并把返回的文件 ID 写回原表单字段。 */
    const uploadMedia = async (event: ChangeEvent<HTMLInputElement>, mediaType: "image" | "video") => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const allowedTypes = mediaType === "image" ? CONSIGNMENT_IMAGE_TYPES : CONSIGNMENT_VIDEO_TYPES;
        const maxSize = mediaType === "image" ? CONSIGNMENT_IMAGE_MAX_SIZE : CONSIGNMENT_VIDEO_MAX_SIZE;
        const fieldName = mediaType === "image" ? "imgMainId" : "videoId";
        const label = mediaType === "image" ? "主图" : "视频";
        if (!allowedTypes.includes(file.type) || file.size > maxSize) {
            setErrorMessage(
                mediaType === "image"
                    ? "主图仅支持 JPEG/PNG，且大小不能超过 2MB"
                    : "视频仅支持 MP4/MOV/MKV，且大小不能超过 200MB"
            );
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        try {
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = (await fileService.uploadFile(formData)) as ConsignmentUploadedFile;
            const fileId = Number(uploadedFile.id);
            if (!Number.isFinite(fileId) || fileId <= 0) throw new Error("上传响应缺少文件 ID");
            // 保持 Vue 的 imgMainId/videoId 字段名，避免改动保存与提交 payload。
            form.setFieldValue(fieldName, fileId);
            if (mediaType === "image") setMainImagePath(uploadedFile.path || file.name);
            else setVideoPath(uploadedFile.path || file.name);
            setErrorMessage(null);
        } catch {
            setErrorMessage(`寄售商品${label}上传失败`);
        }
    };

    /** 打开 Vue address-item 对应的地图选择弹窗，并回显已有 geo 坐标。 */
    const openMapPicker = () => {
        const currentPoint = parseGeoPoint(
            [form.getFieldValue("geoLng"), form.getFieldValue("geoLat")].filter(value => value !== "").join(",")
        );
        setMapDraft(formatGeoPoint(currentPoint));
        setMapDialogOpen(true);
    };

    /** 地图移动后读取中心点，保持旧 Vue 地图拖动更新经纬度的行为。 */
    const handleMapMoveEnd = () => {
        setMapDraft(formatGeoPoint(mapRef.current?.getCenter()));
    };

    /** 确认地图中心点并回填表单字段，提交时仍转换成 Vue geo 对象。 */
    const confirmMapPicker = () => {
        const point = parseGeoPoint(mapDraft);
        if (!point) {
            setErrorMessage("请输入有效经纬度");
            return;
        }
        form.setFieldsValue({ geoLng: String(point[0]), geoLat: String(point[1]) });
        setMapDialogOpen(false);
    };

    const submit = async (isDraft: boolean) => {
        try {
            // Vue 草稿允许不完整字段，正式提交才执行表单必填校验。
            const values = isDraft ? form.getFieldsValue() : await form.validateFields();
            const payload = { ...values };
            // 原生 select 返回字符串，转换为 Vue 接口要求的数值价格类型。
            const rawPriceType = payload.priceType as unknown;
            if (rawPriceType !== undefined && rawPriceType !== "") payload.priceType = Number(rawPriceType);
            const rawWaitingPayTime = payload.waitingPayTime as unknown;
            if (rawWaitingPayTime !== undefined && rawWaitingPayTime !== "") {
                payload.waitingPayTime = Number(rawWaitingPayTime);
            }
            // 原生输入框返回字符串，转换为 Vue 接口要求的媒体文件 ID 数值。
            const rawImgMainId = payload.imgMainId as unknown;
            if (rawImgMainId !== undefined && rawImgMainId !== "") {
                payload.imgMainId = Number(rawImgMainId);
            }
            const rawVideoId = payload.videoId as unknown;
            if (rawVideoId !== undefined && rawVideoId !== "") {
                payload.videoId = Number(rawVideoId);
            }
            // 原生输入框返回字符串，转换为 Vue 接口要求的最小调整吨数数值。
            const rawMinAdjustmentTon = payload.minAdjustmentTon as unknown;
            if (rawMinAdjustmentTon !== undefined && rawMinAdjustmentTon !== "") {
                payload.minAdjustmentTon = Number(rawMinAdjustmentTon);
            }
            // 原生 select/input 返回字符串，转换为 Vue 接口要求的结算数值字段。
            const rawSettlementMethod = payload.settlementMethod as unknown;
            if (rawSettlementMethod !== undefined && rawSettlementMethod !== "") {
                payload.settlementMethod = Number(rawSettlementMethod);
            }
            const rawDepositPaymentMethod = payload.depositPaymentMethod as unknown;
            if (rawDepositPaymentMethod !== undefined && rawDepositPaymentMethod !== "") {
                payload.depositPaymentMethod = Number(rawDepositPaymentMethod);
            }
            const rawPaymentRatio = payload.paymentRatio as unknown;
            if (rawPaymentRatio !== undefined && rawPaymentRatio !== "") {
                payload.paymentRatio = Number(rawPaymentRatio);
            }
            const rawFixedAmount = payload.fixedAmount as unknown;
            if (rawFixedAmount !== undefined && rawFixedAmount !== "") {
                payload.fixedAmount = Number(rawFixedAmount);
            }
            // 原生输入框返回字符串，转换为 Vue 接口要求的供应商 ID 数值。
            const rawSupplierId = payload.supplierId as unknown;
            if (rawSupplierId !== undefined && rawSupplierId !== "") {
                payload.supplierId = Number(rawSupplierId);
            }
            // 参数值使用 JSON 文本快速录入，提交前还原为 Vue 所需的数组结构。
            if (payload.parameterInfoText !== undefined) {
                const parsedParameterInfo: unknown = JSON.parse(payload.parameterInfoText || "[]");
                if (!Array.isArray(parsedParameterInfo)) throw new Error("参数值格式错误");
                payload.parameterInfo = parsedParameterInfo as ConsignmentFormValues["parameterInfo"];
                delete payload.parameterInfoText;
            }
            // 商品标签使用逗号分隔文本，提交前过滤空项并还原为 Vue 字符串数组。
            if (payload.productLabelText !== undefined) {
                payload.productLabel = payload.productLabelText
                    .split(",")
                    .map(label => label.trim())
                    .filter(Boolean);
                delete payload.productLabelText;
            }
            // 专员联系电话使用逗号文本快速录入，提交前还原为 Vue 字符串数组。
            const rawHandlerId = payload.handlerId as unknown;
            if (rawHandlerId !== undefined && rawHandlerId !== "") {
                payload.handlerId = Number(rawHandlerId);
            }
            if (payload.handlerMobileText !== undefined) {
                payload.handlerMobile = payload.handlerMobileText
                    .split(",")
                    .map(mobile => mobile.trim())
                    .filter(Boolean);
                delete payload.handlerMobileText;
            }
            // 地图选择器回填的经纬度在提交前还原为 Vue geo 对象。
            if (payload.geoLng !== undefined || payload.geoLat !== undefined) {
                const lng = payload.geoLng === "" || payload.geoLng === undefined ? undefined : Number(payload.geoLng);
                const lat = payload.geoLat === "" || payload.geoLat === undefined ? undefined : Number(payload.geoLat);
                payload.geo = { lng, lat };
                delete payload.geoLng;
                delete payload.geoLat;
            }
            // Vue 后端接收单值 payType：线上=1、线下=2、线上线下=3。
            const rawPayTypes = payload.payTypes as unknown;
            if (Array.isArray(rawPayTypes)) {
                const payTypes = rawPayTypes.map(item => Number(item));
                payload.payTypes = payTypes;
                payload.payType = payTypes.includes(1) && payTypes.includes(2) ? 3 : payTypes[0];
            }
            // 原生 select 返回字符串，转换为 Vue 接口要求的数值税费类型。
            const rawIsIncludeTax = payload.isIncludeTax as unknown;
            if (rawIsIncludeTax !== undefined && rawIsIncludeTax !== "") {
                payload.isIncludeTax = Number(rawIsIncludeTax);
            }
            const rawTaxRate = payload.taxRate as unknown;
            if (rawTaxRate !== undefined && rawTaxRate !== "") {
                payload.taxRate = Number(rawTaxRate);
            }
            // Vue 保存与提交共用表单数据，但分别命中草稿和正式提交接口。
            await (isDraft ? save : submitForm)(id, payload);
            void navigate("/productManagement/consignment");
        } catch {
            setErrorMessage("寄售商品保存失败");
        }
    };

    return (
        <section aria-label="consignment-form-page">
            <Card>
                <ZhDetailHeader title={id ? "编辑寄售商品" : "新增寄售商品"} />
                {errorMessage ? <p role="alert">{errorMessage}</p> : null}
                <Form form={form} layout="vertical">
                    {/* 使用 zhihao 网格承载现有字段，字段控件和表单契约保持不变。 */}
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem span={2}>
                            <Form.Item
                                label="商品标题"
                                name="title"
                                rules={[{ required: true, message: "请输入商品标题" }]}
                            >
                                <Input />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="权重值" name="weight">
                                <InputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="主图文件 ID" name="imgMainId">
                                <Input aria-label="主图文件 ID" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                上传寄售商品主图
                                <input
                                    aria-label="上传寄售商品主图"
                                    accept="image/jpeg,image/png"
                                    type="file"
                                    onChange={event => void uploadMedia(event, "image")}
                                />
                            </label>
                            {mainImagePath ? <span>主图已上传：{mainImagePath}</span> : null}
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="视频文件 ID" name="videoId">
                                <Input aria-label="视频文件 ID" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                上传寄售商品视频
                                <input
                                    aria-label="上传寄售商品视频"
                                    accept="video/mp4,video/quicktime,video/x-matroska"
                                    type="file"
                                    onChange={event => void uploadMedia(event, "video")}
                                />
                            </label>
                            {videoPath ? <span>视频已上传：{videoPath}</span> : null}
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="单价类型" name="priceType">
                                <select aria-label="单价类型">
                                    <option value="">请选择单价类型</option>
                                    <option value="1">固定价格</option>
                                    <option value="2">预估价格</option>
                                </select>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="等待付款时间" name="waitingPayTime">
                                <select aria-label="等待付款时间">
                                    <option value="">请选择等待付款时间</option>
                                    <option value="1">3小时</option>
                                    <option value="2">12小时</option>
                                    <option value="3">24小时</option>
                                    <option value="4">48小时</option>
                                    <option value="5">72小时</option>
                                    <option value="6">5天</option>
                                    <option value="7">7天</option>
                                    <option value="8">不限时长</option>
                                </select>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="是否含税" name="isIncludeTax">
                                <select aria-label="是否含税">
                                    <option value="">请选择是否含税</option>
                                    <option value="0">不含</option>
                                    <option value="1">含税</option>
                                </select>
                            </Form.Item>
                        </ZhGridItem>
                        {Number(isIncludeTax) === 1 ? (
                            <ZhGridItem>
                                <Form.Item label="税率" name="taxRate">
                                    <select aria-label="税率">
                                        <option value="">请选择税率</option>
                                        <option value="1">3%</option>
                                        <option value="2">6%</option>
                                        <option value="3">9%</option>
                                        <option value="4">13%</option>
                                    </select>
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        <ZhGridItem>
                            <Form.Item label="销售单价" name="unitPrice">
                                <InputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="起订吨数" name="minOrderTon">
                                <InputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="库存量" name="inventoryQuantity">
                                <InputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="提货开始时间" name="deliveryStartTime">
                                <Input aria-label="提货开始时间" type="date" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="提货结束时间" name="deliveryEndTime">
                                <Input aria-label="提货结束时间" type="date" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="最小调整吨数" name="minAdjustmentTon">
                                <Input aria-label="最小调整吨数" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="规格" name="productTypeSpec">
                                <Input />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="商品介绍" name="introduce">
                                <Input.TextArea rows={5} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="提货说明" name="deliveryDescn">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="结算说明" name="settlementDescn">
                                <Input.TextArea aria-label="结算说明" rows={3} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="服务保障" name="serviceGuarantee">
                                <Input.TextArea aria-label="服务保障" rows={3} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="寄售规则" name="regulation">
                                <Input.TextArea aria-label="寄售规则" rows={3} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="下单要求" name="settlementMethod">
                                <select aria-label="下单要求">
                                    <option value="">请选择下单要求</option>
                                    <option value="1">全部货款</option>
                                    <option value="2">支付定金</option>
                                </select>
                            </Form.Item>
                        </ZhGridItem>
                        {Number(settlementMethod) === 2 ? (
                            <ZhGridItem>
                                <Form.Item label="支付定金方式" name="depositPaymentMethod">
                                    <select aria-label="支付定金方式">
                                        <option value="">请选择支付定金方式</option>
                                        <option value="1">货款比例</option>
                                        <option value="2">固定金额</option>
                                    </select>
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        {Number(settlementMethod) === 2 && Number(depositPaymentMethod) === 1 ? (
                            <ZhGridItem>
                                <Form.Item label="货款比例" name="paymentRatio">
                                    <Input aria-label="货款比例" />
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        {Number(settlementMethod) === 2 && Number(depositPaymentMethod) === 2 ? (
                            <ZhGridItem>
                                <Form.Item label="固定金额" name="fixedAmount">
                                    <Input aria-label="固定金额" />
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        <ZhGridItem>
                            <Form.Item label="供应商 ID" name="supplierId">
                                <Input aria-label="供应商 ID" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="品类 ID" name="productTypeId">
                                <Input aria-label="品类 ID" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="参数值 JSON" name="parameterInfoText">
                                <Input.TextArea aria-label="参数值 JSON" rows={3} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="商品标签" name="productLabelText">
                                <Input aria-label="商品标签" placeholder="多个标签用逗号分隔" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="专员 ID" name="handlerId">
                                <Input aria-label="专员 ID" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="专员联系电话" name="handlerMobileText">
                                <Input aria-label="专员联系电话" placeholder="多个号码用逗号分隔" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="采区" name="area">
                                <Input aria-label="采区" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="省编码" name="provinceCode">
                                <Input aria-label="省编码" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="市编码" name="cityCode">
                                <Input aria-label="市编码" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="区编码" name="regionCode">
                                <Input aria-label="区编码" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="提货地址" name="address">
                                <Input aria-label="提货地址" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="经度" name="geoLng">
                                <Input aria-label="经度" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="纬度" name="geoLat">
                                <Input aria-label="纬度" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <ZhButton type="primary" onClick={openMapPicker}>
                                地图选点
                            </ZhButton>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="支付方式" name="payTypes">
                                <Checkbox.Group
                                    aria-label="支付方式"
                                    options={[
                                        { label: "线上支付", value: 1 },
                                        { label: "线下支付", value: 2 }
                                    ]}
                                />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                    <Space>
                        <Button type="primary" onClick={() => void submit(true)}>
                            保存寄售商品
                        </Button>
                        <Button type="primary" onClick={() => void submit(false)}>
                            提交寄售商品
                        </Button>
                        <Button onClick={() => void navigate("/productManagement/consignment")}>返回</Button>
                    </Space>
                </Form>
            </Card>
            <ZhDialog
                open={mapDialogOpen}
                title="位置"
                width={920}
                closeOnClickModal={false}
                onCancel={() => setMapDialogOpen(false)}
                footer={
                    <Space>
                        <ZhButton onClick={() => setMapDialogOpen(false)}>取消</ZhButton>
                        <ZhButton type="primary" onClick={confirmMapPicker}>
                            确定地图位置
                        </ZhButton>
                    </Space>
                }
            >
                <Input
                    aria-label="地图经纬度"
                    placeholder="请输入经度,纬度"
                    value={mapDraft}
                    onChange={event => setMapDraft(event.target.value)}
                />
                <ZhMap
                    ref={mapRef}
                    center={parseGeoPoint(mapDraft) || DEFAULT_MAP_CENTER}
                    className="mt-4 h-[360px] w-full"
                    mapMoveEnd={handleMapMoveEnd}
                    showScale
                />
            </ZhDialog>
        </section>
    );
}

export default ConsignmentFormPage;
